import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import './Auth.css';

const Login = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.login({ username: data.username, password: data.password });
      login(response.data.token, response.data.user);
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Side - Image */}
      <div className="login-left">
        <div className="illustration-section">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=800&fit=crop" 
            alt="Team collaboration" 
            className="hero-image"
          />
          <div className="image-overlay">
            <h3 className="illustration-title">Manage Tasks Efficiently</h3>
            <p className="illustration-text">Collaborate with your team, track progress, and deliver projects on time</p>
          </div>
        </div>
      </div>
      {/* Login Form */}
      <div className="login-right">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">TM</div>
            <h2 className="login-title">Welcome back</h2>
            <p className="login-subtitle">Sign in to manage your tasks and teams</p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label className="form-label">Username</label>
              <input 
                type="text" 
                {...register('username', { required: 'Username is required' })} 
                className="form-input"
                placeholder="Enter your username"
                disabled={loading}
              />
              {errors.username && <span className="error-text">{errors.username.message}</span>}
            </div>

            <div className="form-group">
              <div className="password-header">
                <label className="form-label">Password</label>
                <Link to="/forgot-password" className="forgot-link">Forgot?</Link>
              </div>
              <div className="password-input-wrapper">
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  {...register('password', { required: 'Password is required' })} 
                  className="form-input"
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" className="icon-eye">
                      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="icon-eye-off">
                      <path d="M3 3l18 18M9.88 9.88A3 3 0 0 0 12 17c1.66 0 3-1.34 3-3 0-.88-.36-1.68-.95-2.25M6.5 6.5c-1.5 1.5-2.7 3.3-3.5 5 1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="error-text">{errors.password.message}</span>}
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <p className="security-text">
              <svg viewBox="0 0 24 24" className="icon-lock">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Secure login with encryption
            </p>
          </form>
          
          <p className="signup-link">
            Don't have an account? <Link to="/signup" className="link">Create organization</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
